/*
 * Copyright (c) 2019-2021 LogMeIn
 * All Rights Reserved Worldwide.
 *
 * THIS PROGRAM IS CONFIDENTIAL AND PROPRIETARY TO LOGMEIN
 * AND CONSTITUTES A VALUABLE TRADE SECRET.
 */

import uuid4 from 'uuid4';
import { createLogger } from './LoggerService';
import { getCurrentUrl } from './NavigatorService';
import { MeetingInfo } from './MeetingService';
import { captureException } from '@sentry/browser';
import {
  standaloneUlfEnvConfig,
  standaloneUlfSessionParams,
} from '../context/SessionContext';
import { ReactElement } from 'react';

const log = createLogger('IndexPageReaderService');

export const handoverParamsDomId = 'handoverParams';
export const launchTokensDomId = 'launchTokens';
const sessionTrackingIdPrefix = 'ulf';

export enum FlowType {
  Join = 'join',
  Install = 'install',
  Host = 'host',
}

export enum Product {
  G2M = 'g2m',
  G2W = 'g2w',
  G2T = 'g2t',
}

export enum ThemeType {
  GoToMeetingTheme = 'GoToMeetingTheme',
  GoToTheme = 'GoToTheme',
}

export interface HandoverParams {
  error?: GoToError;
  flowType: FlowType;
  meetingId?: string;
  sessionTrackingId: string;
  meetingInfo: MeetingInfo;
  product: Product;
  firstName?: string;
  theme: GoToMeetingThemeTokens | GoToThemeTokens;
  webAppUrl?: string;
  ulfEnvConfig: EnvConfig;
}

export interface EnvConfig {
  instantJoinBaseUrl: string;
  jwtResolutionDomainBasePath: string;
  jwtOpenerPatchingBaseUrl: string;
  launcherStatusBaseUrl: string;
  telemetryServiceBaseUrl: string;
  engagementServiceBaseUrl: string;
}

interface BasicThemeToken {
  themeType: ThemeType;
}

interface GoToMeetingThemeTokens extends BasicThemeToken {
  themeType: ThemeType.GoToMeetingTheme;
}
interface GoToThemeTokens extends BasicThemeToken {
  themeType: ThemeType.GoToTheme;
}

export interface LaunchTokens {
  launcherServiceLaunchToken: string;
  openerLaunchToken: string;
  endpointToken: string;
}

interface GoToError {
  code: ErrorCode;
  message: string;
}

export type ErrorPagesInterface = {
  [key in ErrorCode]: ReactElement;
};

export enum ErrorCode {
  MissingProperty = 'MissingProperty',
  MissingUserAgent = 'MissingUserAgent',
  MalformedJsonWebToken = 'MalformedJsonWebToken',
  UnsupportedBrowser = 'UnsupportedBrowser',
  BreakoutUnsupportedBrowser = 'BreakoutUnsupportedBrowser',
  E2eeUnsupportedOperatingSystem = 'E2eeUnsupportedOperatingSystem',
  E2eeUnsupportedEapStatus = 'E2eeUnsupportedEapStatus',
}

export const readLaunchTokensFromIndexPage = (): LaunchTokens | null => {
  return readParamsFromIndexPage<LaunchTokens>(launchTokensDomId);
};

export const getHandoverParams = (): HandoverParams => {
  return (
    readParamsFromIndexPage<HandoverParams>(handoverParamsDomId) ??
    createFallbackHandoverParams()
  );
};

// TODO: Remove after branch handling in lambda is in place
const createFallbackHandoverParams = (): HandoverParams => {
  const handoverParams: HandoverParams = {
    flowType: FlowType.Join,
    meetingId:
      readMeetingIdFromPath() ||
      readFromUrl('meetingId') ||
      standaloneUlfSessionParams.meetingId,
    sessionTrackingId:
      readFromUrl('sessionTrackingId') || generateSessionTrackingId(),
    meetingInfo: standaloneUlfSessionParams.meetingInfo,
    product: Product.G2M,
    theme: {
      themeType: ThemeType.GoToTheme,
    },
    ulfEnvConfig: standaloneUlfEnvConfig,
  };

  log.info('Created following fallback handover parameters:', handoverParams);
  return handoverParams;
};

const readParamsFromIndexPage = <T>(elementId: string): T | null => {
  log.info(`Looking for parameters with id "${elementId}" in index page...`);
  let params: T | null;
  try {
    const element = document && document.getElementById(elementId);
    const rawJson = element && element.innerHTML;
    params = rawJson && JSON.parse(rawJson);
  } catch (e) {
    captureException(e);
    params = null;
  }
  log.info('Found parameters:', params);
  return params;
};

const readFromUrl = (key: string): string | null => {
  const url: URL = new URL(getCurrentUrl());
  const value = url.searchParams.get(key);
  log.info('Value for key', key, 'in URL is:', value);
  return value;
};

const readMeetingIdFromPath = (): string | null => {
  const url: URL = new URL(getCurrentUrl());
  const splitPath = url.pathname.split('/join/');
  const meetingId = splitPath && splitPath[1];
  if (!meetingId) {
    return null;
  }
  log.info('MeetingId: ', meetingId);
  return meetingId;
};

const generateSessionTrackingId = (): string => {
  const stid = `${sessionTrackingIdPrefix}-${uuid4()}`;
  log.warn('New sessionTrackingId was generated:', stid);
  return stid;
};
